var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// db/queue-job.ts
import { A } from "@adornis/base/env-info.js";
import { addGlobalHook } from "@adornis/base/globalHooks.js";
import { logger } from "@adornis/base/logging.js";
import { logPerformanceOf } from "@adornis/base/performance-log.js";
import { Int } from "@adornis/baseql/baseqlTypes.js";
import { Entity, Field } from "@adornis/baseql/decorators.js";
import { MongoEntity } from "@adornis/baseql/entities/mongoEntity.js";
import { baseqlMetaData } from "@adornis/baseql/metadata/metaDataStore.js";
import { getRawCollection } from "@adornis/baseql/server/collections.js";
import { runInServerContext } from "@adornis/baseql/server/server.js";
import needle from "needle";
import { Subject, interval, of } from "rxjs";
import { catchError, concatMap } from "rxjs/operators";
import { promisify } from "util";
import { Analytics } from "./analytics.js";
if (A.isServer) {
  const sub = interval(1e3).subscribe(() => QueueJob.queueTrigger.next());
  addGlobalHook("cleanup-analytics", () => {
    sub.unsubscribe();
  });
}
var QueueJob = class extends MongoEntity {
  static get collection() {
    return getRawCollection(QueueJob._collectionName);
  }
  constructor(doc) {
    super(doc);
  }
  async getAnalytics() {
    return this.constructor.fields.analytics?.options.resolve?.bind(this)()(
      Analytics.allFields
    );
  }
  async create() {
    if (!A.isServer)
      throw new Error("Cannot create QueueJobs on client");
    const id = await this.constructor.createQueueJobServerSide(this);
    this._id = id;
    this.commit();
    return id;
  }
  static async createQueueJobServerSide(queueJob) {
    if (!A.isServer)
      throw new Error("ServerSide only method (saveQueueJobServerSide");
    return runInServerContext(async () => {
      let id;
      const collection = await getRawCollection(this._collectionName);
      const existingJobQueue = await collection.findOne({ analyticIDs: queueJob.analyticIDs });
      if (existingJobQueue)
        id = existingJobQueue._id;
      else {
        if (!queueJob._id)
          queueJob._id = queueJob.constructor.resolveID(queueJob) || A.getGloballyUniqueID();
        const result = await collection.insertOne(queueJob.toJSON());
        await this.afterCreate(queueJob, result);
        id = result.insertedId || queueJob._id;
      }
      await (await getRawCollection(Analytics._collectionName)).updateMany({ _class: queueJob.analyticsClass, _id: { $in: queueJob.analyticIDs } }, { $set: { _i: true } });
      QueueJob.queueTrigger.next();
      return id;
    });
  }
};
QueueJob._class = "QueueJob";
QueueJob._collectionName = "job-queue";
QueueJob.queueTrigger = new Subject();
__decorateClass([
  Field((type) => [String])
], QueueJob.prototype, "analyticIDs", 2);
__decorateClass([
  Field((type) => [Analytics], {
    resolve: function() {
      return async (gqlFields) => {
        const entityConstructor = baseqlMetaData.entities.find((node) => node.type()._class === this.analyticsClass)?.type();
        return (await (await getRawCollection(Analytics._collectionName)).find({ _class: this.analyticsClass, _id: { $in: this.analyticIDs } }).toArray()).map((obj) => new entityConstructor(obj));
      };
    }
  })
], QueueJob.prototype, "analytics", 2);
__decorateClass([
  Field((type) => String)
], QueueJob.prototype, "analyticsClass", 2);
__decorateClass([
  Field((type) => Int)
], QueueJob.prototype, "urgency", 2);
__decorateClass([
  Field((type) => Date)
], QueueJob.prototype, "targetDate", 2);
QueueJob = __decorateClass([
  Entity()
], QueueJob);
if (A.isServer) {
  const uniqueIdentifier = `${Date.now()}${Math.random()}`;
  const cleanUpWorkingQueueJobs = async () => {
    (await getRawCollection(QueueJob._collectionName)).updateMany(
      { working: uniqueIdentifier },
      { $unset: { working: "" } }
    );
  };
  const sendDiscordNotificationForUnexpectedProductionEvents = (error, prefix) => {
    if (!A.isProduction)
      return;
    return promisify(needle.post)(
      "https://discord.com/api/webhooks/877557127135264879/3SMNpPbGqPebk0BnQGV8u8lYkMSHAkeKg60fgfjFzmc1WsvMSeoU-x-tD0trv2O3JQuh",
      {
        embeds: [
          {
            title: prefix + error.message.slice(0, 256 - (prefix?.length ?? 0)),
            description: (error.stack || "").slice(0, 4096),
            author: { name: process.env.ROOT_URL },
            color: 48340
          }
        ]
      },
      { json: true }
    );
  };
  let lastWorkedOnQueueJobJSON = null;
  QueueJob.queueTrigger.pipe(
    concatMap(
      () => of(void 0).pipe(
        concatMap(async () => {
          const nextQueueJob = await (await QueueJob.collection).findOneAndUpdate(
            {
              targetDate: { $lt: /* @__PURE__ */ new Date() },
              $or: [{ working: uniqueIdentifier }, { working: { $exists: false } }]
            },
            { $set: { working: uniqueIdentifier } },
            { sort: { urgency: 1 }, returnDocument: "after" }
          );
          if (!nextQueueJob)
            return;
          lastWorkedOnQueueJobJSON = { ...nextQueueJob };
          await logPerformanceOf(`update analytic calculation (${nextQueueJob.analyticsClass})`, async () => {
            const analyticsEntity = baseqlMetaData.entities.find(
              (node) => node.type()._class === nextQueueJob.analyticsClass
            )?.type();
            if (analyticsEntity)
              await analyticsEntity.updateAnalytic(
                new QueueJob(nextQueueJob)
              );
            await (await QueueJob.collection).deleteOne({ _id: nextQueueJob._id });
          })();
        }),
        catchError(async (error) => {
          await sendDiscordNotificationForUnexpectedProductionEvents(error, "Catched and recovered Queue Error: ");
          if (lastWorkedOnQueueJobJSON)
            new QueueJob(lastWorkedOnQueueJobJSON).save();
        })
      )
    )
  ).subscribe({
    next: () => {
    },
    error: async (error) => {
      cleanUpWorkingQueueJobs();
      logger.error({ error: error.message }, "FATAL error while calculating analytic. Analytics queue stopped.");
      await sendDiscordNotificationForUnexpectedProductionEvents(error);
      throw error;
    },
    complete: async () => {
      cleanUpWorkingQueueJobs();
      const error = new Error("JobQueue completed");
      logger.error({ error: error.message }, "FATAL error while calculating analytic. Analytics queue stopped.");
      await sendDiscordNotificationForUnexpectedProductionEvents(error);
      throw error;
    }
  });
}
export {
  QueueJob
};
